import { Injectable } from '@angular/core';
import { AuthorizedApiService, CommonService, LaasyCacheService, shuffleArray } from '@laasy/common-layout';
import { firstValueFrom, forkJoin, interval, map, Observable, of, take, tap } from 'rxjs';
import { AuthenticationService } from 'src/app/shared/services/authentication/authentication.service';
import { TenantService } from 'src/app/shared/services/tenant/tenant.service';
import { SurveyPayload, SurveyResponse } from 'src/app/shared/utilities/interface';

export const SURVEY_TYPE = {
  Signup: 'signup',
  Profile: 'profile',
  UserPref: 'userPref'
}
@Injectable({
  providedIn: 'root'
})
export class SignupSurveyService {

  constructor(private cacheKeyService: LaasyCacheService, private authApi: AuthorizedApiService, private readonly authenticationService: AuthenticationService, private commonService: CommonService, private tenantService: TenantService) { }

  getSurvey(type: string = SURVEY_TYPE.Signup): Observable<any[]> {
    if (type === SURVEY_TYPE.Profile)
      return this.getProfileInCompleteSurvey()
    return forkJoin([
      this.authApi.getAndCacheWithLocal<any>('/user/api/survey', false, 24 * 60 * 60).pipe(take(1)),
      this.tenantService.getTenantByDomain().pipe(take(1))
    ]).pipe(map(([res, tenant]: any) => {
      const activeProductNames: string[] = (tenant?.activeProducts ?? []).map((p: any) => p?.productName?.toLowerCase()).filter(Boolean)
      const data = res.data
      const transformedData: any[] = []
      data.forEach((item: any) => {
        if (item.surveyPresentation.where !== 'Onboarding' && type === SURVEY_TYPE.Signup) return
        const systemName = item.systemName?.toLowerCase() ?? ''
        if (systemName != '' && !activeProductNames.some(name => systemName.includes(name))) return

        const selectionLimit = item.surveyPresentation.maxAnswerSelectionLimit
        const _item: any = {
          que_id: item.questionId, question: item.question, que_type: item.questionType,
          surveyId: item.surveyId, systemName: item.systemName
        }
        if (selectionLimit) _item.selectionLimit = selectionLimit
        const _options: any[] = []
        item.surveyOptionDetails.forEach((option: any) => {
          _options.push({ ...option, img: option.imageUrl, isSelected: false })
        })
        transformedData.push({ ..._item, options: _options.sort((a, b) => a.text.localeCompare(b.text)) })
      })
      return transformedData
    }))
  }

  postSurveyResponse(payload: SurveyPayload[]): void {
    const interval$ = interval(500).subscribe(async _ => {
      const currentSession = await this.authenticationService.GetCurrentUser();
      if (currentSession) {
        interval$.unsubscribe()
        await firstValueFrom(this.updateSurveyResponse(payload))
      }
    })
  }

  updateSurveyResponse(payload: SurveyPayload[]) {
    return this.authApi.post('/user/api/surveyauth/surveyResponse', payload).pipe(tap(() => {
      this.updateUserPrefCache()
    }))
  }

  updateUserPrefCache(): void {
    const url = '/user/api/SurveyAuth/surveyResponses'
    const cacheKey = this.cacheKeyService.getCacheKey(url)
    sessionStorage.removeItem(cacheKey.key)
    this.commonService.getSurvey().subscribe()
  }

  getUserPreference(): Observable<SurveyResponse[]> {
    const getAllSurvey$ = this.getSurvey(SURVEY_TYPE.UserPref)
    const getUserResponse$ = this.commonService.getSurvey().pipe(take(1))

    return forkJoin([getAllSurvey$, getUserResponse$]).pipe(map(([allSurvey, userResponse]) => {
      allSurvey.forEach((survey: any) => {
        let isAnswered = false
        const userSurvey = userResponse.find((user: any) => user.surveyId === survey.surveyId && user.questionId === survey.que_id)
        if (userSurvey) {
          const answerIds = JSON.parse(userSurvey.answerIds)
          answerIds.forEach((answerId: any) => {
            const option = survey.options.find((option: any) => option.id === answerId)
            if (option) {
              option.isSelected = true
              isAnswered = true
            }
          })
        }
        survey.editMode = false
        survey.isUpdated = false
        survey.config = {
          animation: false,
          selectionType: survey.que_type === 'MCQ' ? 'multi' : 'single',
          selectionLimit: (survey.que_type === 'MCQ') ? survey?.selectionLimit ?? survey?.options?.length : 1,
          isAnswered: isAnswered
        }
      })
      return allSurvey
    }))
  }

  getProfileInCompleteSurvey(): Observable<SurveyResponse[]> {
    return this.getUserPreference().pipe(map((res: any) => {
      const incompleteSurvey = res?.filter((item: any) => item?.config?.isAnswered === false) ?? []
      return shuffleArray(incompleteSurvey).slice(0, 2)
    }))
  }

}
